import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { 
  AspectRatio, 
  ComparisonMode, 
  CropCoordinates, 
  ImageData, 
  SecondaryImageData,
  CropRect,
  DragState,
  ContainerDimensions
} from '../types';
import { DEFAULT_DIMENSIONS } from '../config/dimensions';
import { validateImageUrlForEditor } from '../utils/cropUtils';

export const useEditorState = (asset: any, config?: import('../types').EditorConfig) => {
  // Basic editor state
  const [isUploading, setIsUploading] = useState(false);
  const [selectedAspectRatio, setSelectedAspectRatio] = useState<AspectRatio>(() => {
    // Use fixed aspect ratio from config if provided
    if (config?.fixedAspectRatio) {
      return config.fixedAspectRatio;
    }
    return '1:1';
  });
  const [comparisonMode, setComparisonMode] = useState<ComparisonMode>(() => {
    // Use fixed comparison mode from config if provided
    if (config?.fixedComparisonMode) {
      return config.fixedComparisonMode;
    }
    const saved = localStorage.getItem('assetEditor_comparisonMode');
    return (saved as ComparisonMode) || 'single';
  });
  const [showDebugOverlay, setShowDebugOverlay] = useState(false);
  const [containerDimensions, setContainerDimensions] = useState<ContainerDimensions>({ width: 0, height: 0 });
  const [header, setHeader] = useState('');
  const [subheader, setSubheader] = useState('');
  
  // Initialize header and subheader from asset data
  useEffect(() => {
    if (asset) {
      setHeader(asset.header || '');
      setSubheader(asset.subheader || '');
    }
  }, [asset?.header, asset?.subheader]);
  const [isZoomPanLinked, setIsZoomPanLinked] = useState(true);
  const [urlValidationError, setUrlValidationError] = useState<string | null>(null);
  const [isHeaderInputFocused, setIsHeaderInputFocused] = useState(false);
  const [isSubheaderInputFocused, setIsSubheaderInputFocused] = useState(false);
  
  // Helper function to get saved mode
  const getSavedMode = (asset: any, imageType: 'primary' | 'secondary'): 'crop' | 'markup' => {

    // PRIORITY 1: If markup URL exists, ALWAYS use markup mode (ignore saved preference)
    const markupUrl = imageType === 'primary' ? asset.primary_markup_url : asset.secondary_markup_url;
    if (markupUrl) {
      return 'markup';
    }

    // PRIORITY 2: Use saved mode preference if available
    if (asset.crop_coordinates) {
      try {
        const savedData = JSON.parse(asset.crop_coordinates);
        const savedMode = imageType === 'primary' ? savedData.primary?.currentMode : savedData.secondary?.currentMode;
        if (savedMode === 'crop' || savedMode === 'markup') {
          return savedMode;
        }
      } catch (error) {
      }
    }
    
    // PRIORITY 3: Default to crop mode
    return 'crop';
  };

  // Image mode state (crop vs markup) - initialize from saved data or markup existence
  const initialPrimaryMode = getSavedMode(asset, 'primary');
  const initialSecondaryMode = getSavedMode(asset, 'secondary');
  
  
  const [primaryImageMode, setPrimaryImageMode] = useState<'crop' | 'markup'>(initialPrimaryMode);
  const [secondaryImageMode, setSecondaryImageMode] = useState<'crop' | 'markup'>(initialSecondaryMode);

  // CRITICAL FIX: Force markup mode when markup URL exists (only on asset/URL changes, not mode changes)
  useEffect(() => {
    // Clear any previous validation error
    setUrlValidationError(null);
    
    // Validate asset URLs before processing
    if (asset) {
      try {
        // Validate primary URLs if they exist
        if (asset.primary_original_url) {
          validateImageUrlForEditor(asset.primary_original_url, 'primary original URL from asset');
        }
        if (asset.primary_image) {
          validateImageUrlForEditor(asset.primary_image, 'primary image URL from asset');
        }
        if (asset.primary_markup_url) {
          validateImageUrlForEditor(asset.primary_markup_url, 'primary markup URL from asset');
        }

        // Validate secondary URLs if they exist
        if (asset.secondary_original_url) {
          validateImageUrlForEditor(asset.secondary_original_url, 'secondary original URL from asset');
        }
        if (asset.secondary_image) {
          validateImageUrlForEditor(asset.secondary_image, 'secondary image URL from asset');
        }
        if (asset.secondary_markup_url) {
          validateImageUrlForEditor(asset.secondary_markup_url, 'secondary markup URL from asset');
        }
        
      } catch (validationError) {
        // Set the error state instead of throwing (prevents crash)
        const errorMessage = validationError instanceof Error ? validationError.message : String(validationError);
        setUrlValidationError(errorMessage);
        // Return early - don't proceed with mode setting
        return;
      }
    }

    // Force primary to markup mode if markup URL exists but we're not in markup mode
    if (asset?.primary_markup_url && primaryImageMode !== 'markup') {
      setPrimaryImageMode('markup');
    }

    // Force secondary to markup mode if markup URL exists but we're not in markup mode  
    if (asset?.secondary_markup_url && secondaryImageMode !== 'markup') {
      setSecondaryImageMode('markup');
    }
  }, [asset?.primary_original_url, asset?.primary_image, asset?.primary_markup_url, 
      asset?.secondary_original_url, asset?.secondary_image, asset?.secondary_markup_url]); // Depend on all URLs

  // Persist comparison mode to localStorage
  useEffect(() => {
    localStorage.setItem('assetEditor_comparisonMode', comparisonMode);
  }, [comparisonMode]);

  // Image data state with version loading from localStorage or config
  const [primaryImageData, setPrimaryImageData] = useState<ImageData>(() => {
    // Use fixed version from config if provided
    if (config?.fixedVersionData?.primary) {
      return {
        version: config.fixedVersionData.primary.version,
        pageId: config.fixedVersionData.primary.pageId,
        imageUrl: asset.primary_image
      };
    }
    
    const savedPrimaryVersion = localStorage.getItem(`asset_${asset.id}_primary_version`);
    const version = savedPrimaryVersion || asset.version;
    
    return {
      version,
      pageId: asset.page_id,
      imageUrl: asset.primary_image
    };
  });
  
  const [secondaryImageData, setSecondaryImageData] = useState<SecondaryImageData | null>(() => {
    // Use fixed version from config if provided
    if (config?.fixedVersionData?.secondary) {
      return {
        version: config.fixedVersionData.secondary.version,
        pageId: config.fixedVersionData.secondary.pageId,
        imageUrl: asset.secondary_image
      };
    }
    
    const savedSecondaryVersion = localStorage.getItem(`asset_${asset.id}_secondary_version`);
    if (savedSecondaryVersion && asset.secondary_image) {
      return {
        version: savedSecondaryVersion,
        pageId: asset.secondary_page_id || asset.page_id,
        imageUrl: asset.secondary_image
      };
    }
    return null;
  });

  // Helper function to get saved coordinates from explicit database fields
  const getSavedCoordinates = (asset: any) => {
    const defaultCoords = {
      x: 0,
      y: 0,
      scale: 1,
      viewportWidth: DEFAULT_DIMENSIONS.width,
      viewportHeight: DEFAULT_DIMENSIONS.height,
      innerCrop: undefined
    };
    
    // TEMPORARY: Use legacy JSON format until backend supports explicit fields
    if (asset.crop_coordinates) {
      try {
        const savedData = JSON.parse(asset.crop_coordinates);
        return {
          primaryCrop: savedData.primary?.crop || defaultCoords,
          primaryMarkup: savedData.primary?.markup || defaultCoords,
          secondaryCrop: savedData.secondary?.crop || defaultCoords,
          secondaryMarkup: savedData.secondary?.markup || defaultCoords
        };
      } catch (error) {
        // Fall through to default coordinates
      }
    }
    
    // FUTURE: Use explicit fields when backend GraphQL schema is updated
    // const result = {
    //   primaryCrop: asset.primary_crop_coordinates || defaultCoords,
    //   primaryMarkup: asset.primary_markup_coordinates || defaultCoords,
    //   secondaryCrop: asset.secondary_crop_coordinates || defaultCoords,
    //   secondaryMarkup: asset.secondary_markup_coordinates || defaultCoords
    // };
    
    // Default coordinates if no saved data
    return {
      primaryCrop: defaultCoords,
      primaryMarkup: defaultCoords,
      secondaryCrop: defaultCoords,
      secondaryMarkup: defaultCoords
    };
  };

  const savedCoords = getSavedCoordinates(asset);

  // Helper function to check if coordinates are significantly different
  const coordinatesAreDifferent = (coords1: CropCoordinates, coords2: CropCoordinates) => {
    const threshold = 0.1; // Small threshold for floating point comparison
    return (
      Math.abs(coords1.x - coords2.x) > threshold ||
      Math.abs(coords1.y - coords2.y) > threshold ||
      Math.abs(coords1.scale - coords2.scale) > threshold
    );
  };

  // Separate coordinate state for crop vs markup modes - initialize from database
  const [primaryCropCoordinates, setPrimaryCropCoordinates] = useState<CropCoordinates>(savedCoords.primaryCrop);
  const [primaryMarkupCoordinates, setPrimaryMarkupCoordinates] = useState<CropCoordinates>(savedCoords.primaryMarkup);
  const [secondaryCropModeCoordinates, setSecondaryCropModeCoordinates] = useState<CropCoordinates>(savedCoords.secondaryCrop);
  const [secondaryMarkupCoordinates, setSecondaryMarkupCoordinates] = useState<CropCoordinates>(savedCoords.secondaryMarkup);

  // Legacy compatibility - return current mode coordinates (REACTIVE)
  const zoomCoordinates = useMemo(() => {
    return primaryImageMode === 'crop' ? primaryCropCoordinates : primaryMarkupCoordinates;
  }, [primaryImageMode, primaryCropCoordinates, primaryMarkupCoordinates]);
  
  const secondaryZoomCoordinates = useMemo(() => {
    return secondaryImageMode === 'crop' ? secondaryCropModeCoordinates : secondaryMarkupCoordinates;
  }, [secondaryImageMode, secondaryCropModeCoordinates, secondaryMarkupCoordinates]);
  
  
  // Dynamic setter functions that always update the correct mode-specific coordinates
  const setZoomCoordinates = useCallback((coords: CropCoordinates | ((prev: CropCoordinates) => CropCoordinates)) => {
    if (primaryImageMode === 'crop') {
      if (typeof coords === 'function') {
        setPrimaryCropCoordinates(prev => coords(prev));
      } else {
        setPrimaryCropCoordinates(coords);
      }
    } else {
      if (typeof coords === 'function') {
        setPrimaryMarkupCoordinates(prev => coords(prev));
      } else {
        setPrimaryMarkupCoordinates(coords);
      }
    }
  }, [primaryImageMode]);
  
  const setSecondaryZoomCoordinates = useCallback((coords: CropCoordinates | ((prev: CropCoordinates) => CropCoordinates)) => {
    if (secondaryImageMode === 'crop') {
      if (typeof coords === 'function') {
        setSecondaryCropModeCoordinates(prev => coords(prev));
      } else {
        setSecondaryCropModeCoordinates(coords);
      }
    } else {
      if (typeof coords === 'function') {
        setSecondaryMarkupCoordinates(prev => coords(prev));
      } else {
        setSecondaryMarkupCoordinates(coords);
      }
    }
  }, [secondaryImageMode]);

  // Check if current coordinates are different and update link state accordingly
  useEffect(() => {
    // Only check when in dual image mode and we have current coordinates for both images
    if (comparisonMode !== 'single') {
      // Use current live coordinates instead of just saved coordinates
      const currentPrimaryCoords = primaryImageMode === 'crop' ? primaryCropCoordinates : primaryMarkupCoordinates;
      const currentSecondaryCoords = secondaryImageMode === 'crop' ? secondaryCropModeCoordinates : secondaryMarkupCoordinates;

      // Check if current coordinates differ significantly
      const currentCoordsDiffer = coordinatesAreDifferent(currentPrimaryCoords, currentSecondaryCoords);

      // If current coordinates differ significantly, disable linking
      if (currentCoordsDiffer) {
        setIsZoomPanLinked(false);
      }
    }
  }, [comparisonMode, primaryImageMode, secondaryImageMode, primaryCropCoordinates, primaryMarkupCoordinates, secondaryCropModeCoordinates, secondaryMarkupCoordinates]); // Check when any coordinate state changes

  // Keep these for backward compatibility with original component
  const [cropCoordinates, setCropCoordinates] = useState<CropCoordinates>({
    x: 0,
    y: 0,
    scale: 1,
    viewportWidth: DEFAULT_DIMENSIONS.width,
    viewportHeight: DEFAULT_DIMENSIONS.height,
    innerCrop: undefined
  });

  const [secondaryCropCoordinates, setSecondaryCropCoordinates] = useState<CropCoordinates>({
    x: 0,
    y: 0,
    scale: 1,
    viewportWidth: DEFAULT_DIMENSIONS.width,
    viewportHeight: DEFAULT_DIMENSIONS.height,
    innerCrop: undefined
  });

  // Refs for performance optimization
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef<DragState>({ x: 0, y: 0 });
  const isResizingRef = useRef<string | null>(null);
  const renderCountRef = useRef(0);

  // Temporary state for smooth operations
  const [tempCrop, setTempCrop] = useState<CropRect | null>(null);
  const [tempSecondaryCrop, setTempSecondaryCrop] = useState<CropRect | null>(null);
  const tempCropRef = useRef<CropRect | null>(null);
  const tempSecondaryCropRef = useRef<CropRect | null>(null);

  // Reset request flags (will be handled by save handlers)
  const primaryResetRequested = useRef(false);
  const secondaryResetRequested = useRef(false);

  return {
    // Basic state
    isUploading,
    setIsUploading,
    selectedAspectRatio,
    setSelectedAspectRatio,
    comparisonMode,
    setComparisonMode,
    showDebugOverlay,
    setShowDebugOverlay,
    containerDimensions,
    setContainerDimensions,
    header,
    setHeader,
    subheader,
    setSubheader,
    isZoomPanLinked,
    setIsZoomPanLinked,
    
    // Image modes
    primaryImageMode,
    setPrimaryImageMode,
    secondaryImageMode,
    setSecondaryImageMode,

    // Image data
    primaryImageData,
    setPrimaryImageData,
    secondaryImageData,
    setSecondaryImageData,

    // Coordinates (legacy compatibility)
    zoomCoordinates,
    setZoomCoordinates,
    secondaryZoomCoordinates,
    setSecondaryZoomCoordinates,
    cropCoordinates,
    setCropCoordinates,
    secondaryCropCoordinates,
    setSecondaryCropCoordinates,

    // Mode-specific coordinates
    primaryCropCoordinates,
    setPrimaryCropCoordinates,
    primaryMarkupCoordinates,
    setPrimaryMarkupCoordinates,
    secondaryCropModeCoordinates,
    setSecondaryCropModeCoordinates,
    secondaryMarkupCoordinates,
    setSecondaryMarkupCoordinates,

    // Refs
    isDraggingRef,
    dragStartRef,
    isResizingRef,
    renderCountRef,

    // Temporary state
    tempCrop,
    setTempCrop,
    tempSecondaryCrop,
    setTempSecondaryCrop,
    tempCropRef,
    tempSecondaryCropRef,

    // Reset request flags
    primaryResetRequested,
    secondaryResetRequested,

    // Error handling
    urlValidationError,
    setUrlValidationError,

    // Input focus state
    isHeaderInputFocused,
    setIsHeaderInputFocused,
    isSubheaderInputFocused,
    setIsSubheaderInputFocused,

    // Configuration-based control visibility
    canChangeAspectRatio: !config?.fixedAspectRatio,
    canChangeComparisonMode: !config?.fixedComparisonMode,
    showAspectRatioControls: config?.showAspectRatioControls !== false,
    showComparisonModeControls: config?.showComparisonModeControls !== false,
    showVersionSelectors: config?.showVersionSelectors !== false,
    showDebugPanel: config?.showDebugPanel !== false,
    isEmbedded: config?.isEmbedded || false,

    // Config object for components to access
    config
  };
};